import { FeatureType } from "@/geoMap/define/FeatureType";
import GeoJSON from "ol/format/GeoJSON";
import { AttributeValue } from "./AttributeValue";
import { ISearchDateAttributeConvert } from "./ISearchDateAttributeConvert";
import {
  TraVosLayerType,
  TraVosRoadType,
} from "@/geoMap/layerFactory/TraVosLayerName";
import { AttributeView } from "./AttributeView";
import { TraVosCodeUtil } from "./util/TraVosCodeUtil";
import { ExportValue } from "./ExportValue";
import { getLayerKey } from "@/geoMap/LayerName";
import { CqlParameter } from "@/geoMap/net/CqlParameter";
import { WFSGetFeatureManager } from "@/geoMap/net/WFSGetFeatureManager";
import { parseTimeCode } from "./util/TimeCodeUtil";
import { Geometry } from "ol/geom";

/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

/**
 * 対象FeatureType
 */
const FEATURE_TYPE = FeatureType.TraVosCCDHourRealTimeAttribute;

/**
 * 出力JSONファイル名
 */
const VALUE_JSON_FILE_NAME =
  "traf_qa-8-2_yyyyMMdd_HHmmss.geojson";

/**
 * 出力CSVファイル名
 */
const VALUE_CSV_FILE_NAME =
  "traf_qa-8-2_yyyyMMdd_HHmmss.csv";

/**
 * 属性データ
 */
const PROPERTY_MAP = [
  { label: "常時観測点コード", key: "常時観測点コード", isSimple: true },
  {
    label: "収集時間フラグ（5分間／1時間）",
    key: "収集時間フラグ",
    isSimple: true,
  },
  { label: "観測年月日", key: "観測年月日", isSimple: true },
  { label: "時間帯", key: "時間帯", isSimple: true },
  {
    label: "上り・小型交通量(台/時間)",
    key: "上り・小型交通量",
    isSimple: true,
  },
  {
    label: "上り・大型交通量(台/時間)",
    key: "上り・大型交通量",
    isSimple: true,
  },
  {
    label: "上り・小型大型判別不能交通量(台/時間)",
    key: "上り・小型大型判別不能交通量",
    isSimple: true,
  },
  {
    label: "下り・小型交通量(台/時間)",
    key: "下り・小型交通量",
    isSimple: true,
  },
  {
    label: "下り・大型交通量(台/時間)",
    key: "下り・大型交通量",
    isSimple: true,
  },
  {
    label: "下り・小型大型判別不能交通量(台/時間)",
    key: "下り・小型大型判別不能交通量",
    isSimple: true,
  },
  { label: "地方整備局等番号", key: "地方整備局等番号", isSimple: false },
  {
    label: "開発建設部／都道府県コード",
    key: "開発建設部／都道府県コード",
    isSimple: false,
  },
  {
    label: "上り・自動車交通量(台/時間)",
    key: "上り・自動車交通量",
    isSimple: false,
  },
  {
    label: "上り・5分欠測処理フラグ",
    key: "上り・5分欠測処理フラグ",
    isSimple: false,
  },
  {
    label: "下り・自動車交通量(台/時間)",
    key: "下り・自動車交通量",
    isSimple: false,
  },
  {
    label: "下り・5分欠測処理フラグ",
    key: "下り・5分欠測処理フラグ",
    isSimple: false,
  },
];

/**
 * 画面幅
 */
const VALUE_WIDTH = 410;

/**
 * 画面高さ
 */
const VALUE_HEIGTH = 500;

/**
 * 画面最大高さ
 */
const VALUE_MAX_HEIGHT = 490;

/**
 * 画面タイトル
 */
const VALUE_TITLE = "交通量データ（最新）属性表示画面(CCTVトラカン計測値1時間)";

/**
 * リアルタイムTraVos属性表示画面（計測値１時間）（画像認識型）属性取得クラス
 */
export class RealTimeTraVosCCDHourSearchDateAttributeConvert
  implements ISearchDateAttributeConvert
{
  /**
   * GeoJSON
   */
  private _geoJSON: any;
  /**
   * TraVosレイヤ名型定義
   */
  private _layerType: TraVosLayerType;
  /**
   * TraVos道路種別コード
   */
  private _roadType: TraVosRoadType;
  /**
   * 常時観測点コード
   */
  private _regularObservationCd: string;
  /**
   * 収集時間フラグ
   */
  private _timeCorrectionFlag: string;
  /**
   * TraVos道路種別コード
   */
  private _timeCode: string;
  /**
   * 地物通称
   */
  private _commonName: string;

  /**
   * コンストラクタ
   * @param geoJSON GeoJSON
   * @param layerType TraVosレイヤタイプ
   * @param roadType TraVos道路種別
   * @param regularObservationCd 常時観測点
   * @param commonName 通称
   * @param timeCorrectionFlag 収集時間フラグ
   * @param timeCode 時間コード
   */
  constructor(
    geoJSON: any,
    layerType: TraVosLayerType,
    roadType: TraVosRoadType,
    regularObservationCd: string,
    commonName: string,
    timeCorrectionFlag: string,
    timeCode: string
  ) {
    this._geoJSON = geoJSON;
    this._layerType = layerType;
    this._roadType = roadType;
    this._regularObservationCd = regularObservationCd;
    this._commonName = commonName;
    this._timeCorrectionFlag = timeCorrectionFlag;
    this._timeCode = timeCode;
  }
  /**
   * 属性データ更新
   * @param timeCode 時間コード
   */
  // 地物情報をtimeCodeで取得なおして、結果やtimeCodeを保持しておく
  async load(timeCode: string): Promise<ISearchDateAttributeConvert> {
    const layerKey = getLayerKey(this._layerType).replace("v_", "t_");
    const cqlQuery = `"道路種別" = ${this._roadType} AND "時間コード" = ${timeCode} AND "常時観測点コード" = ${this._regularObservationCd} `;
    const cqlParameter = new CqlParameter(layerKey, cqlQuery);
    const WFSGetter = new WFSGetFeatureManager();
    const loadedGeoJSON = await WFSGetter.getInfo(URL_API, [
      cqlParameter,
    ]).catch((error) => {
      throw error;
    });
    return new RealTimeTraVosCCDHourSearchDateAttributeConvert(
      loadedGeoJSON,
      this._layerType,
      this._roadType,
      this._regularObservationCd,
      this._commonName,
      this._timeCorrectionFlag,
      timeCode
    ) as ISearchDateAttributeConvert;
  }

  /**
   * 時間コード取得
   */
  getTimeCode(): string {
    return this._timeCode;
  }

  /**
   * 地物タイプ取得
   */
  getFeatureType(): FeatureType {
    return FEATURE_TYPE;
  }
  /**
   * 画面用フォーマット取得
   */
  getViewFormatData(): AttributeView {
    const attributeValues: AttributeValue[] = [];
    const { year, month, day, hour, minute } = parseTimeCode(this._timeCode);
    const feature = new GeoJSON().readFeatures(this._geoJSON)[0];
    // 該当地物がない場合は、クラス変数から必要項目を取得し、その他は "-"
    if (feature) {
      // 画面項目に基づき値を取得
      PROPERTY_MAP.forEach((property) => {
        if (property.key === "常時観測点コード") {
          /**
           * 常時観測点コード(クラス変数)
           */
          attributeValues.push({
            key: property.label,
            value: `${this._regularObservationCd}（${this._commonName}）`,
            isSimple: property.isSimple,
          });
        } else if (property.key === "収集時間フラグ") {
          /**
           * 収集時間フラグを画面用文字列に変換する（クラス変数）
           */
          attributeValues.push({
            key: property.label,
            value: TraVosCodeUtil.toCollectTimeCodeViewString(
              this._timeCorrectionFlag
            ),
            isSimple: property.isSimple,
          });
        } else if (property.key === "観測年月日") {
          /**
           * 観測年月日
           */
          attributeValues.push({
            key: property.label,
            value: `${year}/${month}/${day}`,
            isSimple: property.isSimple,
          });
        } else if (property.key === "時間帯") {
          /**
           * 時間帯
           */
          attributeValues.push({
            key: property.label,
            value: `${hour}:${minute}`,
            isSimple: property.isSimple,
          });
        } else if (property.key === "地方整備局等番号") {
          /**
           * 地方整備局等番号
           */
          attributeValues.push({
            key: property.label,
            value: TraVosCodeUtil.toRegionalBureauViewString(
              feature.get(property.key)
            ),
            isSimple: property.isSimple,
          });
        } else if (property.key === "開発建設部／都道府県コード") {
          /**
           * 開発建設部／都道府県コード
           */
          attributeValues.push({
            key: property.label,
            value: TraVosCodeUtil.toPrefectureCodeViewString(
              feature.get(property.key)
            ),
            isSimple: property.isSimple,
          });
        } else if (
          property.key === "上り・5分欠測処理フラグ" ||
          property.key === "下り・5分欠測処理フラグ"
        ) {
          /**
           * 5分欠損処理フラグ
           */
          attributeValues.push({
            key: property.label,
            value: TraVosCodeUtil.toFiveMinMissingViewString(
              feature.get(property.key)
            ),
            isSimple: property.isSimple,
          });
        } else {
          attributeValues.push({
            key: property.label,
            value: feature.get(property.key),
            isSimple: property.isSimple,
          });
        }
      });
    } else {
      PROPERTY_MAP.forEach((property) => {
        if (property.key === "常時観測点コード") {
          /**
           * 常時観測点コード(クラス変数)
           */
          attributeValues.push({
            key: property.label,
            value: `${this._regularObservationCd}（${this._commonName}）`,
            isSimple: property.isSimple,
          });
        } else if (property.key === "収集時間フラグ") {
          /**
           * 収集時間フラグを画面用文字列に変換する（クラス変数）
           */
          attributeValues.push({
            key: property.label,
            value: TraVosCodeUtil.toCollectTimeCodeViewString(
              this._timeCorrectionFlag
            ),
            isSimple: property.isSimple,
          });
        } else if (property.key === "観測年月日") {
          /**
           * 観測年月日（クラス変数）
           */
          attributeValues.push({
            key: property.label,
            value: `${year}/${month}/${day}`,
            isSimple: property.isSimple,
          });
        } else if (property.key === "時間帯") {
          /**
           * 時間帯（クラス変数）
           */
          attributeValues.push({
            key: property.label,
            value: `${hour}:${minute}`,
            isSimple: property.isSimple,
          });
        } else {
          attributeValues.push({
            key: property.label,
            value: "-",
            isSimple: property.isSimple,
          });
        }
      });
    }
    const attributeView: AttributeView = {
      width: VALUE_WIDTH,
      height: VALUE_HEIGTH,
      maxheight: VALUE_MAX_HEIGHT,
      title: VALUE_TITLE,
      attributeValues: attributeValues,
    };
    return attributeView;
  }
  /**
   * ファイル出力用項目取得
   */
  getExportValue(): ExportValue {
    const features = new GeoJSON().readFeatures(this._geoJSON);
    if (!features) {
      throw Error("地物が取得できませんでした。");
    } else if (features.length > 1) {
      throw Error("地物が複数取得されました。");
    }
    const feature = features[0];
    feature.getGeometry().transform("EPSG:3857", "EPSG:4326");
    const attributeValues = this.getViewFormatData().attributeValues;
    return new ExportValue(
      feature,
      attributeValues,
      VALUE_JSON_FILE_NAME,
      VALUE_CSV_FILE_NAME
    );
  }

  /**
   * ジオメトリを取得
   */
  getGeometry(): Geometry {
    const feature = new GeoJSON().readFeatures(this._geoJSON)[0];
    return feature?.getGeometry().transform("EPSG:3857", "EPSG:4326");
  }
}
